import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import FullCalendar from '@fullcalendar/react';
import interactionPlugin, {Draggable, EventReceiveArg, EventResizeDoneArg} from "@fullcalendar/interaction";
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import {addDays, addWeeks, format, isValid, parseISO, startOfWeek} from 'date-fns';
import {fetchEventsForRange} from '../data/fetchEvents';
import {Department, DEPARTMENTS, IConflictCheckResponse, IEmployee, IEvent, IFirestoreProductionResource, IHoliday, IProductionZone, ITimeOffRequest} from '../data/interfaces';
import {EventClickArg, EventContentArg, EventDropArg} from '@fullcalendar/core';
import EventModal from "./EventModal";
import {useTheme} from "./ThemeContext";
import {deleteDoc, doc} from 'firebase/firestore';
import {db} from '../firebaseConfig';
import {signOut} from '../utils/authUtils';
import {adjustEndDateForFirestore, augmentEventForCalendar} from "../utils/augmentEventForCalendar";
import {formatDateForInput} from "../utils/formatDateForInput";
import {useUser} from '../context/userContext';
import {userCanEdit} from "../utils/permissions";
import {saveEvent, STALE_WRITE_CONFLICT_ERROR} from "../data/saveEvent";
import {getEmployeeFromUid} from "../data/getEmployeeFromUid";
import {fetchAvailableCrews} from "../data/fetchAvailableCrews";
import Spinner from "./Spinner";
import {useNavigate} from 'react-router-dom';
import Header from "./Header";
import {fetchTimeOffForRange} from "../data/fetchTimeOffRequests";
import {DEPARTMENT_ROUTE_PATH_BY_DEPARTMENT} from "../utils/departmentUtils";
import {fetchHolidaysForRange} from "../data/fetchHolidays";
import {firestoreCollections} from "../data/firestoreConstants";
import {CALENDAR_EVENT_SYMBOLS} from "../utils/calendarEventSymbols";
import {getActiveProductionResources, isProductionResourceArchived, productionResourceBelongsToZone, saveProductionResourcesForZone, subscribeToProductionResources} from "../data/productionResources";
import ProductionResourceManager from "./ProductionResourceManager";
import {BsGearFill} from "react-icons/bs";

interface CalendarProps {
  isCollapsed: boolean;
  toggleSidebar: () => void;
  currentDepartment: Department;
  initialDate?: string;
  unscheduledTravelersCount: number;
  selectedTravelerId?: string;
  setSelectedTravelerId?: (id: string | undefined) => void;
  productionZones?: IProductionZone[];
  selectedProductionZone?: IProductionZone | null;
  setSelectedProductionZoneId?: (id: string) => void;
}

interface Resource {
  id: string;
  title: string;
  sortOrder?: number;
}

const UNASSIGNED_RESOURCE_ID = 'unassigned';

type CalendarDragEventArg = EventDropArg | EventReceiveArg | EventResizeDoneArg;
type PendingOptimisticSave = {
  event: IEvent;
  expectedRevision: number | null;
};
interface CalendarCacheEntry {
  events: IEvent[];
  unsubscribeEvents?: () => void;
  timeOff: ITimeOffRequest[];
  unsubscribeTimeOff?: () => void;
  holidays: IHoliday[];
  unsubscribeHolidays?: () => void;
}

function getCachedEvents(cache: {[cacheKey: string]: CalendarCacheEntry}): IEvent[] {
  const eventsById = new Map<string, IEvent>();

  Object.values(cache)
    .flatMap((cacheEntry) => cacheEntry.events)
    .forEach((event) => {
      const cachedEvent = eventsById.get(event.id);
      if (!cachedEvent || (event.revision ?? 0) > (cachedEvent.revision ?? 0)) {
        eventsById.set(event.id, event);
      }
    });

  return Array.from(eventsById.values());
}

function unsubscribeCalendarCacheEntry(cacheEntry: CalendarCacheEntry): void {
  cacheEntry.unsubscribeEvents?.();
  cacheEntry.unsubscribeTimeOff?.();
  cacheEntry.unsubscribeHolidays?.();
}

/**
 * Generates an array of week start dates based on the current date.
 *
 * @param {Date} currentDate - The reference date.
 * @returns {string[]} - An array of week start dates in 'yyyy-MM-dd' format.
 */
const getWeeksToLoad = (currentDate: Date): string[] => {
  const weeks: string[] = [];
  const currentWeekStart = startOfWeek(currentDate);

  // Previous 2 weeks
  for (let i = -2; i < 0; i++) {
    const weekStart = format(addWeeks(currentWeekStart, i), 'yyyy-MM-dd');
    weeks.push(weekStart);
  }

  // Current week and next 3 weeks
  for (let i = 0; i < 4; i++) {
    const weekStart = format(addWeeks(currentWeekStart, i), 'yyyy-MM-dd');
    weeks.push(weekStart);
  }

  return weeks;
};


/**
 * Constructs the start and end dates for a given week.
 *
 * @param {string} weekStart - The start date of the week in 'yyyy-MM-dd' format.
 * @returns {{ start: Date; end: Date }} - The start and end Date objects for the week.
 */
const getWeekDateRange = (weekStart: string): { start: Date; end: Date } => {
  const start = parseISO(weekStart);
  const end = addWeeks(start, 1);
  end.setDate(end.getDate() - 1); // End of the week (Sunday)
  return {start, end};
};

const eventIntersectsDateRange = (event: IEvent, rangeStart: Date, rangeEnd: Date): boolean => {
  const eventStart = new Date(event.start);
  const eventEnd = new Date(event.end ?? event.start);

  return (
    (eventStart >= rangeStart && eventStart <= rangeEnd) ||
    (eventEnd >= rangeStart && eventEnd <= rangeEnd) ||
    (eventStart < rangeStart && eventEnd > rangeEnd)
  );
};

function productionEventMatchesSelectedZone(
  event: IEvent,
  selectedProductionZone?: IProductionZone | null
): boolean {
  if (event.department !== DEPARTMENTS.production || !selectedProductionZone) {
    return true;
  }

  const traveler = event.traveler;
  if (traveler) {
    return selectedProductionZone.fabCrews.some((fabCrewRef) =>
      fabCrewRef.path === traveler.fabCrew!.path
    );
  }

  return event.productionZone?.path === selectedProductionZone.ref.path;
}

function getProductionEventResourceId(
  event: IEvent,
  productionResourcesById: Map<string, IFirestoreProductionResource>,
  selectedProductionZone?: IProductionZone | null
): string {
  const productionResourceId = event.productionResourceId;

  if (!productionResourceId) {
    return UNASSIGNED_RESOURCE_ID;
  }

  const productionResource = productionResourcesById.get(productionResourceId);

  if (!productionResource) {
    return UNASSIGNED_RESOURCE_ID;
  }

  if (
    selectedProductionZone &&
    !productionResourceBelongsToZone(productionResource, selectedProductionZone)
  ) {
    return UNASSIGNED_RESOURCE_ID;
  }

  if (!isProductionResourceArchived(productionResource)) {
    return productionResourceId;
  }

  const resourceArchivedDate = productionResource.archivedAt
    ? formatDateForInput(productionResource.archivedAt.toDate())
    : null;
  const eventStartDate = formatDateForInput(event.start);

  return resourceArchivedDate && eventStartDate < resourceArchivedDate
    ? productionResourceId
    : UNASSIGNED_RESOURCE_ID;
}

const Calendar: React.FC<CalendarProps> = ({
                                             isCollapsed,
                                             toggleSidebar,
                                             currentDepartment,
                                             initialDate,
                                             unscheduledTravelersCount,
                                             selectedTravelerId,
                                             setSelectedTravelerId,
                                             productionZones = [],
                                             selectedProductionZone,
                                             setSelectedProductionZoneId,
                                           }) => {
  const {user} = useUser(); // Get authenticated user
  const navigate = useNavigate();
  const {theme, toggleTheme} = useTheme();

  // Parse and validate the initial date
  const parsedDate = initialDate ? parseISO(initialDate) : new Date();
  const validDate = isValid(parsedDate) ? parsedDate : new Date();

  // State variables
  const [currentDate, setCurrentDate] = useState<Date>(validDate);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isProductionResourceManagerOpen, setIsProductionResourceManagerOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<IEvent | null>(null);
  const [availableCrews, setAvailableCrews] = useState<IEmployee[]>([]);
  const [confirmedProductionResources, setConfirmedProductionResources] = useState<IFirestoreProductionResource[]>([]);
  const [optimisticProductionResources, setOptimisticProductionResources] = useState<IFirestoreProductionResource[] | null>(null);
  const [jumpToDate, setJumpToDate] = useState<string>('');
  const [currentUserEmployeeData, setCurrentUserEmployeeData] = useState<IEmployee | null>(null);
  const formattedDate = format(currentDate, 'yyyy-MM-dd');
  const isProductionCalendar = currentDepartment === DEPARTMENTS.production;
  const productionResources = optimisticProductionResources ?? confirmedProductionResources;
  const sortedProductionResources = useMemo(
    () => [...productionResources].sort((a, b) => {
      if (a.sortOrder !== b.sortOrder) {
        return a.sortOrder - b.sortOrder;
      }

      return a.name.localeCompare(b.name);
    }),
    [productionResources]
  );
  const filteredProductionResources = useMemo(
    () => selectedProductionZone
      ? sortedProductionResources.filter((resource) =>
        productionResourceBelongsToZone(resource, selectedProductionZone)
      )
      : sortedProductionResources,
    [selectedProductionZone, sortedProductionResources]
  );
  const activeProductionResources = useMemo(
    () => getActiveProductionResources(filteredProductionResources),
    [filteredProductionResources]
  );
  const productionResourcesById = useMemo(
    () => new Map(sortedProductionResources.map((resource) => [resource.id, resource])),
    [sortedProductionResources]
  );
  const resourceAreaHeaderLabel = isProductionCalendar ? 'Resource' : 'Crew';
  const topResourceAreaHeaderContent = isProductionCalendar
    ? (
      <div className="calendar-resource-header">
        <span>Resource</span>
        <button
          type="button"
          className="btn btn-sm calendar-resource-manage-button"
          onClick={() => setIsProductionResourceManagerOpen(true)}
          disabled={!selectedProductionZone}
          title={selectedProductionZone ? 'manage resources' : 'select a production zone'}
        >
          <BsGearFill className="calendar-resource-manage-icon"/>
          Manage
        </button>
      </div>
    )
    : resourceAreaHeaderLabel;

  // Refs for FullCalendar instances
  const calendarRef1 = useRef<FullCalendar | null>(null);
  const calendarRef2 = useRef<FullCalendar | null>(null);

  // Reference for debouncing
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Reference to store cached events to avoid redundant fetches
  const eventCache = useRef<{[cacheKey: string]: CalendarCacheEntry}>({});
  const saveRequestVersionsByEventId = useRef<Record<string, number>>({});

  // State to hold events from Firestore listeners (server-confirmed baseline)
  const [confirmedEvents, setConfirmedEvents] = useState<IEvent[]>([]);
  const [pendingOptimisticSavesByEventId, setPendingOptimisticSavesByEventId] = useState<Record<string, PendingOptimisticSave>>({});
  const [loadedTimeOff, setLoadedTimeOff] = useState<ITimeOffRequest[]>([]);
  const [loadedHolidays, setLoadedHolidays] = useState<IHoliday[]>([]);
  const confirmedEventsRef = useRef<IEvent[]>([]);

  useEffect(() => {
    confirmedEventsRef.current = confirmedEvents;
  }, [confirmedEvents]);

  // Rendered events = server-confirmed baseline + optimistic overlays.
  const loadedEvents = useMemo(() => {
    let mergedEvents = confirmedEvents;
    Object.values(pendingOptimisticSavesByEventId).forEach((pendingSave) => {
      const optimisticEvent = pendingSave.event;
      const hasExistingEvent = mergedEvents.some((loadedEvent) => loadedEvent.id === optimisticEvent.id);

      if (hasExistingEvent) {
        mergedEvents = mergedEvents.map((loadedEvent) =>
          loadedEvent.id === optimisticEvent.id ? optimisticEvent : loadedEvent
        );
        return;
      }

      mergedEvents = [...mergedEvents, optimisticEvent];
    });

    return mergedEvents;
  }, [confirmedEvents, pendingOptimisticSavesByEventId]);

  // Clear optimistic overlays once Firestore confirms a newer revision for the event.
  useEffect(() => {
    const activeCacheKeys = Object.keys(eventCache.current);

    const isEventInActiveCacheWindows = (event: IEvent): boolean => {
      return activeCacheKeys.some((cacheKey) => {
        const [weekStart, cacheDepartment] = cacheKey.split('_');
        if (event.department !== 'all' && event.department !== cacheDepartment) {
          return false;
        }
        const {start: weekStartDate, end: weekEndDate} = getWeekDateRange(weekStart);
        return eventIntersectsDateRange(event, weekStartDate, weekEndDate);
      });
    };

    setPendingOptimisticSavesByEventId((currentPendingSaves) => {
      const confirmedEventsById = new Map(
        confirmedEvents.map((confirmedEvent) => [confirmedEvent.id, confirmedEvent])
      );
      let hasChanges = false;
      const nextPendingSaves: Record<string, PendingOptimisticSave> = {...currentPendingSaves};

      Object.entries(currentPendingSaves).forEach(([eventId, pendingSave]) => {
        const confirmedEvent = confirmedEventsById.get(eventId);
        if (!confirmedEvent) {
          // Keep optimistic entries scoped to currently subscribed cache windows.
          // If an event cannot appear in any active snapshot window, clear it to
          // avoid stale optimistic state carrying across long-lived navigation.
          if (!isEventInActiveCacheWindows(pendingSave.event)) {
            delete nextPendingSaves[eventId];
            hasChanges = true;
          }
          return;
        }

        if (pendingSave.expectedRevision === null) {
          delete nextPendingSaves[eventId];
          hasChanges = true;
          return;
        }

        const confirmedRevision = confirmedEvent.revision ?? 0;
        if (confirmedRevision > pendingSave.expectedRevision) {
          delete nextPendingSaves[eventId];
          hasChanges = true;
        }
      });

      return hasChanges ? nextPendingSaves : currentPendingSaves;
    });
  }, [confirmedEvents]);

  // Fetch current user employee data
  useEffect(() => {
    const fetchUserData = async () => {
      if (!user) return;
      const data = await getEmployeeFromUid(user.uid);
      setCurrentUserEmployeeData(data ?? null);
    }

    fetchUserData();
  }, [user]);

  /**
   * Filters the given events based on the current department.
   * Memoized to prevent unnecessary recalculations.
   */
  const filterEvents = useCallback((events: IEvent[]) =>
      events.filter((event: IEvent) =>
        (
          event.department === currentDepartment
          && productionEventMatchesSelectedZone(event, selectedProductionZone)
        )
        || event.department === 'all'
      ),
    [currentDepartment, selectedProductionZone]
  );

  /**
   * Updates the URL with the new date.
   * Memoized to prevent unnecessary re-creations.
   */
  const updateURLDate = useCallback((newDate: Date) => {
    const formattedDate = format(newDate, 'yyyy-MM-dd');
    const departmentPath = DEPARTMENT_ROUTE_PATH_BY_DEPARTMENT[currentDepartment];
    navigate(`/${departmentPath}/${formattedDate}`, {replace: true});
  }, [currentDepartment, navigate]);

  /**
   * Handles the jump to date functionality.
   */
  const handleJumpToDate = useCallback(() => {
    if (jumpToDate) {
      const newDate = parseISO(jumpToDate);
      if (isValid(newDate)) {
        // Update the URL, which will trigger the calendar to update via useEffect
        updateURLDate(newDate);
      } else {
        // Handle invalid date input
        alert('Please enter a valid date.');
      }
    } else {
      // If the date is cleared, revert to the current date
      updateURLDate(new Date());
    }
  }, [jumpToDate, updateURLDate]);

  /**
   * Checks if a particular week is in the loading state.
   *
   * @param {Date} currentDate - The reference date.
   * @param {1 | 2} weekIndex - The index of the week (1 or 2).
   * @returns {boolean} - True if the week is loading, false otherwise.
   */
  const isWeekLoading = useCallback((currentDate: Date, weekIndex: 1 | 2): boolean => {
    const weekStartDate = startOfWeek(currentDate);
    const weekStartString = weekIndex === 1 ? format(weekStartDate, 'yyyy-MM-dd') : format(addWeeks(weekStartDate, 1), 'yyyy-MM-dd');
    const cacheKey = `${weekStartString}_${currentDepartment}`;
    return !(cacheKey in eventCache.current);
  }, [currentDepartment]);

  /**
   * Effect hook to fetch and cache events whenever dependencies change.
   */
  useEffect(() => {
    const weeksToLoad = getWeeksToLoad(currentDate);

    // Cleanup cache: unsubscribe and remove entries that are no longer needed
    Object.keys(eventCache.current).forEach((cacheKey) => {
      const [weekStart, department] = cacheKey.split('_');
      if (!weeksToLoad.includes(weekStart) || department !== currentDepartment) {
        unsubscribeCalendarCacheEntry(eventCache.current[cacheKey]);
        delete eventCache.current[cacheKey];
      }
    });

    const DEBOUNCE_DELAY = 300;

    // Clear any existing timeout to prevent multiple fetches
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Set up a new timeout to fetch and cache events after the debounce delay
    debounceTimeoutRef.current = setTimeout(() => {
      const fetchAndCacheEvents = async () => {
        await Promise.all(
          weeksToLoad.map(async (weekStart) => {
            const cacheKey = `${weekStart}_${currentDepartment}`;
            if (!(cacheKey in eventCache.current)) {
              // Initialize cache entry
              eventCache.current[cacheKey] = {
                events: [],
                timeOff: [],
                holidays: [],
              };

              const {start: fetchStartDate, end: fetchEndDate} = getWeekDateRange(weekStart);

              const onEventsUpdate = (events: IEvent[]) => {
                if (!eventCache.current[cacheKey]) return;

                // Update the cache
                eventCache.current[cacheKey].events = events;

                // Aggregate all loaded events from the cache to update the state
                setConfirmedEvents(getCachedEvents(eventCache.current));
              };

              const onTimeOffUpdate = (timeOffRequests: ITimeOffRequest[]) => {
                if (!eventCache.current[cacheKey]) return;

                // Update the cache
                eventCache.current[cacheKey].timeOff = timeOffRequests;

                // Aggregate all loaded events from the cache to update the state
                const allLoadedTimeOff = Object.values(eventCache.current).flatMap(
                  (cacheEntry) => cacheEntry.timeOff
                );
                setLoadedTimeOff(allLoadedTimeOff);
              };

              const onHolidaysUpdate = (holidays: IHoliday[]) => {
                if (!eventCache.current[cacheKey]) return;

                eventCache.current[cacheKey].holidays = holidays;

                const allLoadedHolidays = Object.values(eventCache.current).flatMap(
                  (cacheEntry) => cacheEntry.holidays
                );
                setLoadedHolidays(allLoadedHolidays);
              };

              try {
                // Fetch events for week and store unsubscribe function
                eventCache.current[cacheKey].unsubscribeEvents = await fetchEventsForRange(
                  fetchStartDate,
                  fetchEndDate,
                  currentDepartment,
                  onEventsUpdate
                );
              } catch (error) {
                console.error('Error fetching events:', error);
              }

              try {
                // Fetch time off for week and store unsubscribe function
                eventCache.current[cacheKey].unsubscribeTimeOff = await fetchTimeOffForRange(
                  fetchStartDate,
                  fetchEndDate,
                  currentDepartment,
                  onTimeOffUpdate
                );
              } catch (error) {
                console.error('Error fetching time off:', error);
              }

              try {
                eventCache.current[cacheKey].unsubscribeHolidays = await fetchHolidaysForRange(
                  fetchStartDate,
                  fetchEndDate,
                  onHolidaysUpdate
                );
              } catch (error) {
                console.error('Error fetching holidays:', error);
              }
            }
          })
        );

        // After fetching, aggregate all loaded events from the cache to update the state
        setConfirmedEvents(getCachedEvents(eventCache.current));

        // After fetching, aggregate all loaded time off from the cache to update the state
        const allLoadedTimeOff = Object.values(eventCache.current).flatMap(
          (cacheEntry) => cacheEntry.timeOff
        );
        setLoadedTimeOff(allLoadedTimeOff);

        const allLoadedHolidays = Object.values(eventCache.current).flatMap(
          (cacheEntry) => cacheEntry.holidays
        );
        setLoadedHolidays(allLoadedHolidays);
      };

      // Initiate the fetch and cache process
      // promise ignored intentionally
      fetchAndCacheEvents();
    }, DEBOUNCE_DELAY);

    // Cleanup function to clear the timeout
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [currentDate, currentDepartment]);

  /**
   * Effect to unsubscribe from all listeners when component unmounts
   */
  useEffect(() => {
    return () => {
      Object.values(eventCache.current).forEach((cacheEntry) => {
        unsubscribeCalendarCacheEntry(cacheEntry);
      });
      eventCache.current = {};
    };
  }, []);

  /**
   * Effect to load available crews when the current department changes
   */
  useEffect(() => {
    const loadAvailableCrews = async () => {
      const crews = await fetchAvailableCrews(currentDepartment);
      setAvailableCrews(crews);
    };

    if (currentDepartment === DEPARTMENTS.production) {
      setAvailableCrews([]);
      return;
    }

    loadAvailableCrews();
  }, [currentDepartment]);

  /**
   * Effect to load Production resources when the Production calendar is active.
   */
  useEffect(() => {
    if (currentDepartment !== DEPARTMENTS.production) {
      setConfirmedProductionResources([]);
      setOptimisticProductionResources(null);
      return;
    }

    return subscribeToProductionResources(
      (resources) => {
        setConfirmedProductionResources(resources);
        setOptimisticProductionResources(null);
      },
      (error) => {
        console.error(`Error fetching ${DEPARTMENTS.production} resources:`, error);
        setConfirmedProductionResources([]);
        setOptimisticProductionResources(null);
      }
    );
  }, [currentDepartment]);

  const handleProductionResourcesSave = (resourcesToSave: IFirestoreProductionResource[]) => {
    if (!selectedProductionZone) {
      return;
    }

    setOptimisticProductionResources(resourcesToSave);

    void saveProductionResourcesForZone(selectedProductionZone, resourcesToSave).catch((error: Error) => {
      console.error(`Error saving ${DEPARTMENTS.production} resources:`, error);
      window.alert('Resource changes could not be saved. Please try again.');
      setOptimisticProductionResources(null);
    });
  };

  /**
   * Memoized computation of combined resources to prevent unnecessary recalculations
   */
  const combinedResources = useMemo(() => {
    const firstDayShown = startOfWeek(currentDate);
    const lastDayShown = addWeeks(firstDayShown, 1);
    lastDayShown.setDate(lastDayShown.getDate() + 6);

    const today = new Date();
    const offResource = {id: 'off', title: 'Off'};
    const unassignedResource = {id: UNASSIGNED_RESOURCE_ID, title: 'Unassigned'};

    function dedupeAndSortResources(resources: Resource[]): Resource[] {
      const uniqueResources = resources.reduce((acc: Resource[], resource) => {
        if (!acc.some(existing => existing.id === resource.id)) {
          acc.push(resource);
        }
        return acc;
      }, []);

      return uniqueResources.sort((a, b) => {
        if (a.id === 'off') return 1;  // 'off' always goes to the end
        if (b.id === 'off') return -1; // 'off' always goes to the end
        if (a.id === UNASSIGNED_RESOURCE_ID) return 1;  // 'unassigned' goes just before 'off'
        if (b.id === UNASSIGNED_RESOURCE_ID) return -1; // 'unassigned' goes just before 'off'
        return a.id.localeCompare(b.id); // alphabetical for all other resources
      });
    }

    const configuredProductionResources = activeProductionResources.map((resource) => ({
      id: resource.id,
      title: resource.name,
      sortOrder: resource.sortOrder,
    }));

    const historicalArchivedProductionResources = filterEvents(loadedEvents).reduce((archivedResources: Resource[], event) => {
      if (!eventIntersectsDateRange(event, firstDayShown, lastDayShown)) {
        return archivedResources;
      }

      const productionResourceId = getProductionEventResourceId(
        event,
        productionResourcesById,
        selectedProductionZone
      );
      const productionResource = productionResourcesById.get(productionResourceId);

      if (
        productionResource &&
        (!selectedProductionZone || productionResourceBelongsToZone(productionResource, selectedProductionZone)) &&
        isProductionResourceArchived(productionResource) &&
        !archivedResources.some(resource => resource.id === productionResource.id)
      ) {
        archivedResources.push({
          id: productionResource.id,
          title: productionResource.name,
          sortOrder: productionResource.sortOrder,
        });
      }

      return archivedResources;
    }, []);

    const scheduledEventResources = filterEvents(loadedEvents).reduce((acc: Resource[], event) => {
      const eventStart = new Date(event.start);
      const eventEnd = new Date(event.end ?? event.start);

      const isEventInRange = (
        (eventStart >= firstDayShown && eventStart <= lastDayShown) ||
        (eventEnd >= firstDayShown && eventEnd <= lastDayShown) ||
        (eventStart < firstDayShown && eventEnd > lastDayShown)
      );

      if (isEventInRange && event.resourceId && !acc.some(resource => resource.id === event.resourceId)) {
        acc.push({
          id: event.resourceId,
          title: event.resourceId,
        });
      }

      return acc;
    }, []);
    const eventResources = currentDepartment === DEPARTMENTS.production
      ? [...configuredProductionResources, ...historicalArchivedProductionResources]
      : scheduledEventResources;

    const crewResources = availableCrews.map(crew => ({
      id: crew.preferredName,
      title: crew.preferredName,
    }));

    const departmentsThatShowAllCrews: Department[] = [DEPARTMENTS.installation, DEPARTMENTS.assembly];
    const shouldShowCrewResources =
      departmentsThatShowAllCrews.includes(currentDepartment) &&
      (today <= lastDayShown || firstDayShown > today);
    const shouldShowOffResource = !(
      currentDepartment === DEPARTMENTS.production &&
      /\boutsource\b/i.test(selectedProductionZone?.name ?? '')
    );

    const resources = currentDepartment === DEPARTMENTS.production
      ? [
        ...eventResources,
        {...unassignedResource, sortOrder: Number.MAX_SAFE_INTEGER - 1},
        ...(shouldShowOffResource ? [{...offResource, sortOrder: Number.MAX_SAFE_INTEGER}] : []),
      ]
      : [
        ...eventResources,
        ...(shouldShowCrewResources ? crewResources : []),
        unassignedResource,
        offResource,
      ];

    return dedupeAndSortResources(resources);
  }, [loadedEvents, availableCrews, activeProductionResources, productionResourcesById, currentDate, currentDepartment, selectedProductionZone, filterEvents]);

  /**
   * Retries stale-conflict saves for the latest request with a fresh revision.
   */
  const saveWithLatestStaleRetryAsync = useCallback(async (
    eventDetails: IEvent,
    requestVersion: number,
    initialExpectedRevision: number | null
  ): Promise<void> => {
    let expectedRevisionForAttempt = initialExpectedRevision;
    let hasRetriedStaleConflict = false;

    while (true) {
      try {
        await saveEvent(eventDetails, user, {expectedRevision: expectedRevisionForAttempt});
        return;
      } catch (error) {
        const errorMessage = typeof (error as {message?: unknown})?.message === 'string'
          ? (error as {message: string}).message
          : '';
        const isStaleConflict = errorMessage === STALE_WRITE_CONFLICT_ERROR || errorMessage === 'stale-write-conflict';
        const isLatestRequest = saveRequestVersionsByEventId.current[eventDetails.id] === requestVersion;

        if (!isStaleConflict || !isLatestRequest || hasRetriedStaleConflict) {
          throw error;
        }

        const staleConflictError = error as Error & { currentRevision?: number };
        const confirmedRevision = confirmedEventsRef.current.find(
          (confirmedEvent) => confirmedEvent.id === eventDetails.id
        )?.revision;
        const nextExpectedRevision = typeof staleConflictError.currentRevision === 'number'
          ? staleConflictError.currentRevision
          : typeof confirmedRevision === 'number'
            ? confirmedRevision
            : typeof expectedRevisionForAttempt === 'number'
              ? expectedRevisionForAttempt + 1
              : 0;
        expectedRevisionForAttempt = nextExpectedRevision;
        hasRetriedStaleConflict = true;

        setPendingOptimisticSavesByEventId((currentPendingSaves) => {
          const currentPendingSave = currentPendingSaves[eventDetails.id];
          if (!currentPendingSave) {
            return currentPendingSaves;
          }

          return {
            ...currentPendingSaves,
            [eventDetails.id]: {
              ...currentPendingSave,
              expectedRevision: nextExpectedRevision,
            },
          };
        });
      }
    }
  }, [user]);

  /**
   * Handles the save event function.
   * Memoized to prevent unnecessary re-creations.
   */
  const handleSaveFunction = useCallback(async (eventDetails: IEvent) => {
    let eventToSave: IEvent;
    if (eventDetails.department === DEPARTMENTS.production) {
      const productionResource = productionResourcesById.get(eventDetails.productionResourceId ?? '');
      eventToSave = {
        ...eventDetails,
        productionZone: eventDetails.traveler
          ? null
          : productionResource?.productionZone ?? selectedProductionZone?.ref ?? null,
      };
    } else {
      eventToSave = {
        ...eventDetails,
        productionZone: null,
      };
    }

    const requestVersion = (saveRequestVersionsByEventId.current[eventToSave.id] ?? 0) + 1;
    saveRequestVersionsByEventId.current[eventToSave.id] = requestVersion;
    const confirmedEventForSave = confirmedEventsRef.current.find(
      (confirmedEvent) => confirmedEvent.id === eventToSave.id
    );
    const expectedRevision = confirmedEventForSave ? (confirmedEventForSave.revision ?? 0) : null;
    const optimisticEvent: IEvent = confirmedEventForSave
      ? {
        ...eventToSave,
        revision: confirmedEventForSave.revision ?? 0,
      }
      : eventToSave;

    setPendingOptimisticSavesByEventId((currentPendingSaves) => ({
      ...currentPendingSaves,
      [eventToSave.id]: {
        event: optimisticEvent,
        expectedRevision,
      },
    }));

    try {
      await saveWithLatestStaleRetryAsync(eventToSave, requestVersion, expectedRevision);
    } catch (error) {
      // A newer save request for this event is already in-flight or has completed.
      // Skip stale rollback and stale alerts to avoid clobbering newer state.
      if (saveRequestVersionsByEventId.current[eventToSave.id] !== requestVersion) {
        return;
      }

      setPendingOptimisticSavesByEventId((currentPendingSaves) => {
        if (!(eventToSave.id in currentPendingSaves)) {
          return currentPendingSaves;
        }
        const nextPendingSaves = {...currentPendingSaves};
        delete nextPendingSaves[eventToSave.id];
        return nextPendingSaves;
      });

      const errorMessage = typeof (error as {message?: unknown})?.message === 'string'
        ? (error as {message: string}).message
        : '';
      if (errorMessage === STALE_WRITE_CONFLICT_ERROR || errorMessage === 'stale-write-conflict') {
        alert('This event was updated elsewhere. Please try again.');
      } else {
        alert('Failed to save event. Please try again.');
      }
      throw error;
    }
  }, [productionResourcesById, saveWithLatestStaleRetryAsync, selectedProductionZone]);

  /**
   * Handles the event click event.
   * Memoized to prevent unnecessary re-creations.
   */
  const handleEventClick = useCallback((info: EventClickArg) => {
    const event = info.event;
    const selectedEvent = loadedEvents.find(loadedEvent => event.id === loadedEvent.id);
    if (selectedEvent) {
      setSelectedEvent(selectedEvent);
      setIsModalOpen(true);
    }
  }, [loadedEvents]);

  /**
   * Handles the event drag event.
   * Memoized to prevent unnecessary re-creations.
   */
  const handleEventDrag = useCallback(async (info: CalendarDragEventArg) => {
    const event = info.event;
    const selectedEvent = loadedEvents.find(loadedEvent => event.id === loadedEvent.id);

    if (!selectedEvent) return;

    // Prevent dragging of "Off" events
    if (event.getResources().some((resource: {id: string}) => resource.id === 'off')) {
      info.revert();
      alert("You cannot move 'Off' events.");
      return;
    }

    const eventStart = event.start ? new Date(event.start) : null;
    const eventEnd = event.end ? new Date(event.end) : eventStart; // Use event.start if event.end is not available

    const canEdit = currentUserEmployeeData && eventStart && eventEnd
      ? userCanEdit(eventStart, currentUserEmployeeData, currentDepartment)
      : false;

    if (!canEdit) {
      info.revert(); // Revert the event to its original position
      alert("You do not have permission to move this event.");
      return;
    }

    const newResource = event.getResources()[0];
    const newResourceId = newResource?.id;
    const isTeamEvent = selectedEvent.eventType === 'Holiday' || selectedEvent.eventType === 'Team Meeting';
    const isProductionResourceDropTarget =
      selectedEvent.department === DEPARTMENTS.production &&
      newResourceId &&
      newResourceId !== UNASSIGNED_RESOURCE_ID;

    if (
      isProductionResourceDropTarget &&
      !activeProductionResources.some((resource) => resource.id === newResourceId)
    ) {
      const archivedProductionResource = productionResourcesById.get(newResourceId);
      const archivedProductionResourceLabel = archivedProductionResource
        ? `The ${archivedProductionResource.name} resource`
        : 'This resource';

      info.revert();
      alert(`${archivedProductionResourceLabel} has been archived and is no longer available for assignment.`);
      return;
    }

    const newCrew = !isTeamEvent && newResourceId
      ? availableCrews.find(crew => crew.preferredName === newResourceId) ?? null
      : selectedEvent.crew;
    const productionResourceId = newResourceId === UNASSIGNED_RESOURCE_ID
      ? null
      : newResourceId ?? selectedEvent.productionResourceId;

    const modifiedEvent: IEvent = {
      ...selectedEvent,
      start: event.start ? formatDateForInput(event.start) : selectedEvent.start,
      end: event.end ? adjustEndDateForFirestore(event.end) : selectedEvent.end,
      resourceId: newResourceId ?? selectedEvent.resourceId,
      crew: isTeamEvent ? null : newCrew,
      ...(selectedEvent.department === DEPARTMENTS.production && {
        productionResourceId,
      }),
    };

    // Revert the resource change if it's a team event
    if (isTeamEvent && modifiedEvent.crew?.preferredName !== newResourceId) {
      const resourceId = modifiedEvent.crew?.preferredName ?? UNASSIGNED_RESOURCE_ID;
      event.setResources([resourceId]);
    }

    try {
      await handleSaveFunction(modifiedEvent);
    } catch (error) {
      // Drag/resize handlers are UI event callbacks; catch to avoid unhandled rejections.
      info.revert();
    }
  }, [loadedEvents, currentDepartment, currentUserEmployeeData, availableCrews, activeProductionResources, productionResourcesById, handleSaveFunction]);

  /**
   * Handles the event delete event.
   * Memoized to prevent unnecessary re-creations.
   */
  const handleEventDelete = useCallback(async (eventDetails: IEvent) => {
    try {
      const eventRef = doc(db, firestoreCollections.events, eventDetails.id);
      await deleteDoc(eventRef);
      console.log('Event deleted from Firebase:', eventDetails);

      // Update local state to remove the deleted event
      setConfirmedEvents((prevEvents) => prevEvents.filter(event => event.id !== eventDetails.id));
      setPendingOptimisticSavesByEventId((currentPendingSaves) => {
        if (!(eventDetails.id in currentPendingSaves)) {
          return currentPendingSaves;
        }
        const nextPendingSaves = {...currentPendingSaves};
        delete nextPendingSaves[eventDetails.id];
        return nextPendingSaves;
      });
    } catch (error) {
      console.error('Error deleting event from Firebase:', error);
    }
  }, []);

  /**
   * Handles the close modal event.
   * Memoized to prevent unnecessary re-creations.
   */
  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedEvent(null);
    if (setSelectedTravelerId) {
      setSelectedTravelerId(undefined);
    }
  }, [setSelectedTravelerId]);

  /**
   * Handles the duplicate event.
   * Memoized to prevent unnecessary re-creations.
   */
  const handleDuplicate = useCallback(async (duplicatedEvent: IEvent) => {
    await handleSaveFunction(duplicatedEvent);
  }, [handleSaveFunction]);


  /**
   * Handles the calendar navigation.
   * Memoized to prevent unnecessary re-creations.
   */
  const handleCalendarNavigation = useCallback((action: 'prev' | 'next') => {
    if (calendarRef1.current && calendarRef2.current) {
      const calendarApi1 = calendarRef1.current.getApi();
      const calendarApi2 = calendarRef2.current.getApi();

      // Perform the action (prev or next)
      calendarApi1[action]();
      calendarApi2[action]();

      // Update the current date state
      const newCurrentDate = calendarApi1.getDate();
      setCurrentDate(newCurrentDate);

      // Update the URL with the new date
      updateURLDate(newCurrentDate);
    }
  }, [updateURLDate]);

  const handlePrev = useCallback(() => handleCalendarNavigation('prev'), [handleCalendarNavigation]);
  const handleNext = useCallback(() => handleCalendarNavigation('next'), [handleCalendarNavigation]);

  /**
   * Handles the today button click.
   * Memoized to prevent unnecessary re-creations.
   */
  const handleToday = useCallback(() => {
    const today = new Date();
    const nextWeek = addWeeks(today, 1);
    if (calendarRef1.current && calendarRef2.current) {
      const calendarApi1 = calendarRef1.current.getApi();
      const calendarApi2 = calendarRef2.current.getApi();
      calendarApi1.gotoDate(today);
      calendarApi2.gotoDate(nextWeek);
      setCurrentDate(today);

      // Update the URL with today's date
      updateURLDate(today);
    }
  }, [updateURLDate]);

  // Effect to open modal if a traveler is selected
  useEffect(() => {
    if (selectedTravelerId) {
      setSelectedEvent(null);
      setIsModalOpen(true);
    }
  }, [selectedTravelerId]);

  /**
   * Handles the new event click event.
   * Memoized to prevent unnecessary re-creations.
   */
  const handleNewEventClick = useCallback(() => {
    setSelectedEvent(null);
    setIsModalOpen(true);
  }, []);

  /**
   * Checks for conflicts between the given event and the loaded events.
   * Memoized to prevent unnecessary re-creations.
   */
  const checkForConflicts = useCallback((event: IEvent): IConflictCheckResponse => {
    const localEvent = loadedEvents.find(loadedEvent => loadedEvent.id === event.id) || null;

    if (!localEvent) return {safeToSave: true};

    if (event.lastModifiedBy && localEvent.lastModifiedBy) {
      return {
        safeToSave: event.lastModifiedBy.time >= localEvent.lastModifiedBy.time,
        lastModifiedBy: localEvent.lastModifiedBy
      };
    } else {
      return {safeToSave: true};
    }
  }, [loadedEvents]);

  const renderEventContent = useCallback((eventInfo: EventContentArg) => {
    const title = eventInfo.event.title || '';
    const shipOnlyMarker = CALENDAR_EVENT_SYMBOLS.shipOnly;

    const titleContent = !title.includes(shipOnlyMarker) ? (
      <span>{title}</span>
    ) : (
      <>
        {title.split(shipOnlyMarker).map((part, index) => (
          <React.Fragment key={`title-part-${index}`}>
            {index > 0 && (
              <span title="ship only">{shipOnlyMarker}</span>
            )}
            {part}
          </React.Fragment>
        ))}
      </>
    );

    return <span>{titleContent}</span>;
  }, []);

  /**
   * Initializes draggable external events.
   */
  useEffect(() => {
    const externalEvents = document.getElementById('external-events');
    if (externalEvents) {
      new Draggable(externalEvents, {
        itemSelector: '.fc-event',
        eventData: function (eventEl: HTMLElement) {
          const titleAttribute = eventEl.getAttribute('data-event');
          const parsedData = titleAttribute ? JSON.parse(titleAttribute) : {};

          return {
            ...parsedData,
            duration: {days: 1}
          };
        }
      });
    }
  }, []);

  /**
   * Effect to handle initial date setup and calendar navigation
   */
  useEffect(() => {
    if (initialDate) {
      const parsedDate = parseISO(initialDate);
      if (isValid(parsedDate)) {
        setCurrentDate(parsedDate);

        // Update the calendars to show the selected date and the following week
        if (calendarRef1.current && calendarRef2.current) {
          const calendarApi1 = calendarRef1.current.getApi();
          const calendarApi2 = calendarRef2.current.getApi();
          calendarApi1.gotoDate(parsedDate);
          calendarApi2.gotoDate(addWeeks(parsedDate, 1));
        }
      }
    }
  }, [initialDate]);


  /**
   * Memoized list of augmented events to prevent unnecessary recalculations.
   */
  const uniqueTimeOffRequests = useMemo(() => {
    const seen = new Set<string>();
    return loadedTimeOff.filter(timeOff => {
      const key = `${timeOff.employeeName}-${timeOff.startDate}-${timeOff.endDate}`;
      if (seen.has(key)) {
        return false; // Skip duplicates
      }
      seen.add(key);
      return true;
    });
  }, [loadedTimeOff]);

  const augmentedEvents = useMemo(() => {
    const augmentedRegularEvents = filterEvents(loadedEvents).flatMap(event => {
      const augmentedEvent = augmentEventForCalendar(event);

      if (event.eventType === 'Holiday' || event.eventType === 'Team Meeting') {
        const originalEvent = {...augmentedEvent};
        const backgroundEvent = {...augmentedEvent};
        backgroundEvent.id = `${backgroundEvent.id}_background`;
        delete backgroundEvent.resourceId;
        backgroundEvent.display = 'background';
        return [originalEvent, backgroundEvent];
      } else {
        augmentedEvent.resourceId = event.department === DEPARTMENTS.production
          ? getProductionEventResourceId(event, productionResourcesById, selectedProductionZone)
          : event.resourceId || UNASSIGNED_RESOURCE_ID;
        return augmentedEvent;
      }
    });

    const augmentedTimeOffRequests = uniqueTimeOffRequests.map(timeOff => ({
      title: timeOff.employeeName,
      start: timeOff.startDate,
      end: timeOff.endDate,
      resourceId: 'off',
      editable: false,
      durationEditable: false,
      startEditable: false,
      overlap: false,
      classNames: ['time-off-event'],
      backgroundColor: timeOff.backgroundColor,
      borderColor: timeOff.borderColor,
      textColor: timeOff.textColor,
    }));

    const augmentedHolidays = loadedHolidays.flatMap(holiday => {
      const holidayEvent: IEvent = {
        id: `imported-holiday-${holiday.id}`,
        start: holiday.date,
        end: holiday.date,
        crew: null,
        notes: null,
        customText: holiday.name,
        department: 'all',
        eventType: 'Holiday',
        traveler: null,
      };
      const augmentedHoliday = {
        ...augmentEventForCalendar(holidayEvent),
        editable: false,
        startEditable: false,
        durationEditable: false,
      };
      const backgroundHoliday = {...augmentedHoliday};
      backgroundHoliday.id = `${backgroundHoliday.id}_background`;
      delete backgroundHoliday.resourceId;
      backgroundHoliday.display = 'background';

      return [augmentedHoliday, backgroundHoliday];
    });

    return [...augmentedRegularEvents, ...augmentedTimeOffRequests, ...augmentedHolidays];
  }, [filterEvents, loadedEvents, productionResourcesById, selectedProductionZone, uniqueTimeOffRequests, loadedHolidays]);

  return (
    <div id="calendar-container">

      {/* Render the Header component and pass necessary props */}
      <Header
        toggleSidebar={toggleSidebar}
        isCollapsed={isCollapsed}
        handlePrev={handlePrev}
        handleToday={handleToday}
        handleNext={handleNext}
        jumpToDate={jumpToDate}
        setJumpToDate={setJumpToDate}
        handleJumpToDate={handleJumpToDate}
        handleNewEventClick={handleNewEventClick}
        unscheduledTravelersCount={unscheduledTravelersCount} // Example count, can be dynamic
        formattedDate={formattedDate}
        currentDepartment={currentDepartment}
        productionZones={productionZones}
        selectedProductionZone={selectedProductionZone}
        setSelectedProductionZoneId={setSelectedProductionZoneId}
        signOut={signOut}
        theme={theme}
        toggleTheme={toggleTheme}
      />

      {/* Main calendar view */}
      <div className="calendar-content overflow-auto vh-97">
        {/* Top Calendar */}
        <div
          id="top-calendar"
          className={`position-relative mb-2 ${isProductionCalendar ? 'production-calendar' : ''}`}
          style={{minHeight: '35vh'}}
        >
          {isWeekLoading(currentDate, 1) && (
            <div
              className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-black bg-opacity-25"
              style={{zIndex: 100, minHeight: '30vh'}}
            >
              <Spinner/>
            </div>
          )}
          <FullCalendar
            key={`top-${currentDepartment}`}
            ref={calendarRef1}
            plugins={[interactionPlugin, resourceTimelinePlugin]}
            droppable={true}
            editable={true}
            eventClick={handleEventClick}
            eventContent={renderEventContent}
            events={augmentedEvents}
            eventDrop={handleEventDrag}
            eventReceive={handleEventDrag}
            eventResize={handleEventDrag}
            height={"auto"}
            hiddenDays={[0]}
            initialView='resourceTimelineWeek'
            initialDate={format(startOfWeek(currentDate), 'yyyy-MM-dd')}
            resourceAreaHeaderContent={topResourceAreaHeaderContent}
            resourceAreaWidth="8%"
            resources={combinedResources} // Use combined resources for both weeks
            resourceOrder="sortOrder"
            selectable={true}
            schedulerLicenseKey='CC-Attribution-NonCommercial-NoDerivatives'
            slotDuration={{day: 1}}
            slotLabelFormat={{
              day: 'numeric',
              weekday: 'short'
            }}
            headerToolbar={{
              left: '',
              center: 'title',
              right: ''
            }}
            customButtons={{
              customPrev: {
                text: 'prevButton',
                click: handlePrev,
              },
              customNext: {
                text: 'nextButton',
                click: handleNext,
              },
              customToday: {
                text: 'Today',
                click: handleToday,
              },
            }}
          />
        </div>

        {/* Bottom Calendar */}
        <div
          id="bottom-calendar"
          className={`position-relative ${isProductionCalendar ? 'production-calendar' : ''}`}
          style={{minHeight: '35vh'}}
        >
          {isWeekLoading(currentDate, 2) && (
            <div
              className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-black bg-opacity-25"
              style={{zIndex: 100, minHeight: '30vh'}}
            >
              <Spinner/>
            </div>
          )}
          <FullCalendar
            key={`bottom-${currentDepartment}`}
            ref={calendarRef2}
            plugins={[interactionPlugin, resourceTimelinePlugin]}
            initialView='resourceTimelineWeek'
            initialDate={format(addWeeks(currentDate, 1), 'yyyy-MM-dd')}
            droppable={true}
            editable={true}
            events={augmentedEvents}
            eventClick={handleEventClick}
            eventContent={renderEventContent}
            eventDrop={handleEventDrag}
            eventReceive={handleEventDrag}
            eventResize={handleEventDrag}
            resourceAreaHeaderContent={resourceAreaHeaderLabel}
            resourceAreaWidth="8%"
            resources={combinedResources}
            resourceOrder="sortOrder"
            selectable={true}
            schedulerLicenseKey='CC-Attribution-NonCommercial-NoDerivatives'
            hiddenDays={[0]}
            height={"auto"}
            slotDuration={{day: 1}}
            slotLabelFormat={{
              day: 'numeric',
              weekday: 'short'
            }}
            headerToolbar={{
              left: '',
              center: 'title',
              right: ''
            }}
            titleFormat={{
              year: 'numeric',
              month: 'short',
              day: '2-digit',
            }}
          />
        </div>
      </div>

      {/* Event Modal */}
      <EventModal
        show={isModalOpen}
        handleCloseModal={handleCloseModal}
        handleSave={handleSaveFunction}
        event={selectedEvent}
        department={currentDepartment}
        availableCrews={availableCrews}
        productionResources={activeProductionResources}
        selectedProductionZone={selectedProductionZone}
        defaultEventDate={formatDateForInput(currentDate.getDay() === 0 ? addDays(currentDate, 1) : currentDate)}
        deleteEvent={handleEventDelete}
        handleDuplicate={handleDuplicate}
        checkForConflicts={checkForConflicts}
        employeeData={currentUserEmployeeData}
        travelerId={selectedTravelerId}
      />
      {selectedProductionZone && (
        <ProductionResourceManager
          show={isProductionResourceManagerOpen}
          resources={filteredProductionResources}
          selectedProductionZone={selectedProductionZone}
          onClose={() => setIsProductionResourceManagerOpen(false)}
          onSave={handleProductionResourcesSave}
        />
      )}
    </div>
  );
};

export default Calendar;
